import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { getAppType } from "@/hooks/useCapacitorApp";
import { Button } from "@/components/ui/button";

interface BusinessProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('supplier' | 'supplier_owner' | 'venue_owner' | 'admin' | 'super_admin')[];
}

export const BusinessProtectedRoute = ({ 
  children, 
  allowedRoles = ['supplier', 'supplier_owner', 'venue_owner', 'admin', 'super_admin'] 
}: BusinessProtectedRouteProps) => {
  const { user, userRole, loading, signOut } = useAuth();
  const location = useLocation();
  const isProApp = getAppType() === 'pro';

  // CRITICAL: Wait for auth to fully load AND for userRole to resolve
  // before making any redirect decisions. Without this, a logged-in user
  // briefly has user!=null but userRole==null (role fetch is async),
  // which would otherwise bounce them away from the dashboard.
  if (loading || (user && userRole === null)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-4">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to business login with return URL (query kept, so emailed ?listing=…&tab=… links survive sign-in)
    return <Navigate to={isProApp ? '/pro/login' : `/business/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  // Check if user has an allowed role
  const hasAllowedRole = userRole && (allowedRoles as string[]).includes(userRole);
  
  if (!hasAllowedRole) {
    if (isProApp) {
      return <div role="alert" className="p-6 space-y-4">
        <h1 className="text-xl font-semibold">A business account is needed</h1>
        <p>Sign in with the account you use for your wedding business.</p>
        <Button onClick={() => void signOut()}>Sign out</Button>
      </div>;
    }
    // Redirect to appropriate dashboard based on role
    if (userRole === 'couple') {
      return <Navigate to="/couple/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
